/**
 * @fileoverview Filter Input Component
 * 
 * This component renders different types of filter inputs based on the filter definition.
 * Supports text, select, date-range, number-range, and boolean filter types.
 * 
 * Requirements: 3.3, 3.4
 */

import React, { useState, useCallback, useEffect } from 'react';

/**
 * Filter Input Component
 * 
 * @param {Object} props - Component props
 * @param {FilterDefinition} props.definition - Filter definition
 * @param {*} props.value - Current filter value
 * @param {FilterOperator} props.operator - Current filter operator
 * @param {function(*): void} props.onChange - Value change handler
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {string} props.testId - Test ID
 */
export const FilterInput = ({
  definition,
  value,
  operator,
  onChange,
  disabled = false,
  testId = 'filter-input'
}) => {
  const [localValue, setLocalValue] = useState(value || getDefaultValue());
  const [validationError, setValidationError] = useState(null);

  /**
   * Get default value based on filter type and operator
   */
  function getDefaultValue() {
    if (operator?.requiresArray) {
      return [];
    }
    
    switch (definition.type) {
      case 'boolean':
        return false;
      case 'number-range':
        return operator?.type === 'between' ? [0, 100] : 0;
      case 'date-range':
        return operator?.type === 'between' ? [null, null] : null;
      case 'select':
        return operator?.requiresArray ? [] : null;
      case 'text':
      default:
        return '';
    }
  }

  /**
   * Update local value when prop value changes
   */
  useEffect(() => {
    setLocalValue(value || getDefaultValue());
  }, [value, operator]);

  /**
   * Validate input value
   */
  const validateValue = useCallback((val) => {
    if (!definition.validation) return { valid: true, errors: [] };

    const errors = [];
    
    for (const rule of definition.validation) {
      switch (rule.type) {
        case 'required':
          if (!val || (Array.isArray(val) && val.length === 0)) {
            errors.push(rule.message);
          }
          break;
          
        case 'minLength':
          if (typeof val === 'string' && val.length < rule.value) {
            errors.push(rule.message);
          }
          break;
          
        case 'maxLength':
          if (typeof val === 'string' && val.length > rule.value) {
            errors.push(rule.message);
          }
          break;
          
        case 'min':
          if (typeof val === 'number' && val < rule.value) {
            errors.push(rule.message);
          }
          break;
          
        case 'max':
          if (typeof val === 'number' && val > rule.value) {
            errors.push(rule.message);
          }
          break;
          
        case 'pattern':
          if (typeof val === 'string' && !new RegExp(rule.value).test(val)) {
            errors.push(rule.message);
          }
          break;
          
        case 'custom':
          if (rule.validator && !rule.validator(val)) {
            errors.push(rule.message);
          }
          break;
      }
    }

    return { valid: errors.length === 0, errors };
  }, [definition.validation]);

  /**
   * Handle value change with validation
   */
  const handleValueChange = useCallback((newValue) => {
    setLocalValue(newValue);
    
    // Validate the new value
    const validation = validateValue(newValue);
    setValidationError(validation.valid ? null : validation.errors[0]);
    
    // Only call onChange if validation passes or if clearing the value
    if (validation.valid || !newValue) {
      onChange(newValue);
    }
  }, [validateValue, onChange]);

  /**
   * Render text input
   */
  const renderTextInput = () => (
    <input
      type="text"
      value={localValue || ''}
      onChange={(e) => handleValueChange(e.target.value)}
      placeholder={definition.placeholder}
      disabled={disabled}
      className={`filter-text-input ${validationError ? 'error' : ''}`}
      data-testid={`${testId}-text`}
    />
  );

  /**
   * Render select input
   */
  const renderSelectInput = () => {
    const isMultiple = operator?.requiresArray;
    
    if (isMultiple) {
      return (
        <div className="filter-multi-select">
          {definition.options?.map((option) => (
            <label key={option.value} className="filter-checkbox-option">
              <input
                type="checkbox"
                checked={Array.isArray(localValue) && localValue.includes(option.value)}
                onChange={(e) => {
                  const currentArray = Array.isArray(localValue) ? localValue : [];
                  const newArray = e.target.checked
                    ? [...currentArray, option.value]
                    : currentArray.filter(v => v !== option.value);
                  handleValueChange(newArray);
                }}
                disabled={disabled || option.disabled}
                data-testid={`${testId}-checkbox-${option.value}`}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      );
    }

    return (
      <select
        value={localValue || ''}
        onChange={(e) => handleValueChange(e.target.value || null)}
        disabled={disabled}
        className={`filter-select-input ${validationError ? 'error' : ''}`}
        data-testid={`${testId}-select`}
      >
        <option value="">Select {definition.label}</option>
        {definition.options?.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </select>
    );
  };

  /**
   * Render number range input
   */
  const renderNumberRangeInput = () => {
    const isBetween = operator?.type === 'between';
    
    if (isBetween) {
      const rangeValue = Array.isArray(localValue) ? localValue : [0, 100];
      
      return (
        <div className="filter-number-range">
          <input
            type="number"
            value={rangeValue[0] || ''}
            onChange={(e) => {
              const newValue = [Number(e.target.value) || 0, rangeValue[1]];
              handleValueChange(newValue);
            }}
            placeholder="Min"
            disabled={disabled}
            className={`filter-number-input ${validationError ? 'error' : ''}`}
            data-testid={`${testId}-number-min`}
          />
          <span className="range-separator">to</span>
          <input
            type="number"
            value={rangeValue[1] || ''}
            onChange={(e) => {
              const newValue = [rangeValue[0], Number(e.target.value) || 0];
              handleValueChange(newValue);
            }}
            placeholder="Max"
            disabled={disabled}
            className={`filter-number-input ${validationError ? 'error' : ''}`}
            data-testid={`${testId}-number-max`}
          />
        </div>
      );
    }

    return (
      <input
        type="number"
        value={localValue || ''}
        onChange={(e) => handleValueChange(Number(e.target.value) || null)}
        placeholder={definition.placeholder}
        disabled={disabled}
        className={`filter-number-input ${validationError ? 'error' : ''}`}
        data-testid={`${testId}-number`}
      />
    );
  };

  /**
   * Render date range input
   */
  const renderDateRangeInput = () => {
    const isBetween = operator?.type === 'between';
    
    if (isBetween) {
      const rangeValue = Array.isArray(localValue) ? localValue : [null, null];
      
      return (
        <div className="filter-date-range">
          <input
            type="date"
            value={rangeValue[0] || ''}
            onChange={(e) => {
              const newValue = [e.target.value || null, rangeValue[1]];
              handleValueChange(newValue);
            }}
            disabled={disabled}
            className={`filter-date-input ${validationError ? 'error' : ''}`}
            data-testid={`${testId}-date-start`}
          />
          <span className="range-separator">to</span>
          <input
            type="date"
            value={rangeValue[1] || ''}
            onChange={(e) => {
              const newValue = [rangeValue[0], e.target.value || null];
              handleValueChange(newValue);
            }}
            disabled={disabled}
            className={`filter-date-input ${validationError ? 'error' : ''}`}
            data-testid={`${testId}-date-end`}
          />
        </div>
      );
    }

    return (
      <input
        type="date"
        value={localValue || ''}
        onChange={(e) => handleValueChange(e.target.value || null)}
        disabled={disabled}
        className={`filter-date-input ${validationError ? 'error' : ''}`}
        data-testid={`${testId}-date`}
      />
    );
  };

  /**
   * Render boolean input
   */
  const renderBooleanInput = () => (
    <div className="filter-boolean-input">
      <label className="filter-boolean-label">
        <input
          type="checkbox"
          checked={!!localValue}
          onChange={(e) => handleValueChange(e.target.checked)}
          disabled={disabled}
          data-testid={`${testId}-boolean`}
        />
        <span>{definition.label}</span>
      </label>
    </div>
  );

  /**
   * Render the appropriate input based on filter type
   */
  const renderInput = () => {
    switch (definition.type) {
      case 'text':
        return renderTextInput();
      case 'select':
        return renderSelectInput();
      case 'number-range':
        return renderNumberRangeInput();
      case 'date-range':
        return renderDateRangeInput();
      case 'boolean':
        return renderBooleanInput();
      default:
        return renderTextInput();
    }
  };

  return (
    <div className={`filter-input ${definition.type}`} data-testid={testId}>
      {renderInput()}
      
      {validationError && (
        <div className="filter-validation-error" data-testid={`${testId}-error`}>
          {validationError}
        </div>
      )}
    </div>
  );
};
